<template>
	<div>
		<div id="nav" class="nav" :class="{ 'nav--light': showLightNav, 'nav--dark': showDarkNav }">
			<!-- This is the nav bar at the top of the screen -->
			<div class="nav-bar" :class="{ 'nav-bar--active': dropDownIsOpen || showBackdrop, 'nav-bar--scrolled': scrollPointReached }">
				<!-- Left side of the nav bar -->
				<div class="nav-bar__left">
					<!-- The hamburger menu link, it is only visible on mobile -->
					<a href="#" class="nav-bar__menu" @click.prevent="toggleDropdown('menuDropdownIsOpen')">
						<div class="hamburger" :class="{ 'hamburger--active': menuDropdownIsOpen }">
							<div class="hamburger__line" />
							<div class="hamburger__line" />
							<div class="hamburger__line" />
						</div>
					</a>

					<!-- Only show these links on desktop -->
					<a v-if="linkPermissions.products" class="nav-bar__link nav-bar__link--desktop" @click="toggleDropdown('productsDropdownIsOpen')">
						Products
						<div class="nav-bar__down-caret" :class="{ 'nav-bar__down-caret--active': productsDropdownIsOpen }" />
					</a>
					<!-- <a class="nav-bar__link nav-bar__link--desktop" @click.prevent="toggleDropdown('searchDropdownIsOpen')">
						Search
						<div class="nav-bar__down-caret" :class="{ 'nav-bar__down-caret--active': searchDropdownIsOpen }" />
					</a> -->
				</div>

				<!-- The avana wholesale logo -->
				<router-link :to="{ name: 'home' }" @click.native="closeAllDropdowns">
					<AvanaLogo />
				</router-link>

				<!-- Right side of the navebar -->
				<div class="nav-bar__right">
					<!-- Only show these links on desktop -->
					<a class="nav-bar__link nav-bar__link--desktop" @click.prevent="toggleDropdown('accountDropdownIsOpen')">
						{{ dropdownText }}
						<div class="nav-bar__down-caret" :class="{ 'nav-bar__down-caret--active': accountDropdownIsOpen }" />
					</a>

					<!-- Cart icon / count. You should only be able to view this if you have the permission -->
					<a v-if="linkPermissions.cart" class="nav-bar__cart" @click="toggleCart()">
						<span class="nav-bar__link nav-bar__link--desktop">Cart</span>
						<div class="nav-bar__cart-icon">
							<span>{{ cartCount }}</span>
						</div>
					</a>
				</div>
			</div>

			<!-- The products dropdown -->
			<!-- We also want to detect if you have reached the scroll point with this div as we want it to be the remaning height of the window -->
			<div class="nav__products" :class="{ 'nav__products--active': productsDropdownIsOpen, 'nav__products--scrolled': scrollPointReached }">
				<div class="products">
					<div class="products__left">
						<!-- Iterate through all of the product navigation cards -->
						<router-link
							v-for="productClass in productClasses"
							:key="productClass.name"
							:to="{ name: 'products', query: { class: productClass.name } }"
							class="products__card"
							@click.native="closeAllDropdowns"
						>
							<div class="card__container">
								<div class="card__image-wrapper">
									<img :src="`/images/${productClass.name}.png`" :alt="productClass.name" class="card__image" />
								</div>
								<div class="card__content">
									<div class="card__title">{{ productClass.name }}</div>
									<div v-if="productClass.subtitle" class="card__subtitle">{{ productClass.subtitle }}</div>
								</div>
							</div>
						</router-link>
					</div>
					<router-link class="products__right" :to="{ name: 'products' }" @click.native="closeAllDropdowns">
						<div class="right__container">
							<AvanaLetter />
							<span class="right__text">Shop All</span>
						</div>
					</router-link>
				</div>
			</div>

			<!-- The search dropdown -->
			<!-- <div class="nav__search" :class="{ 'nav__search--active': searchDropdownIsOpen }">
				<div class="search">
					Why hello there. General kenobi
				</div>
			</div> -->

			<!-- The account/admin dropdown -->
			<div class="nav__account" :class="{ 'nav__account--active': accountDropdownIsOpen }">
				<div class="account">
					<div class="account__container">
						<!-- Iterate through all of the links, these will either be admin or account links depending upon who the user is -->
						<template v-for="(link, index) in dropdownLinks">
							<router-link v-if="linkPermissions[link.permission]" :to="{ name: link.url_name }" class="account__link" :key="index">{{
								link.text
							}}</router-link>
						</template>

						<!-- General links -->
						<router-link :to="{ name: 'settings' }" class="account__link">Settings</router-link>
						<router-link :to="{ name: 'logout' }" class="account__link">Sign Out</router-link>
					</div>
				</div>
			</div>

			<!-- The mobile menu dropdown -->
			<div class="nav__menu" :class="{ 'nav__menu--active': menuDropdownIsOpen }">
				<div class="menu">
					<!-- Menu main links -->
					<div class="menu__links">
						<!-- Open and close the products collapse -->
						<a
							v-if="linkPermissions.products"
							class="menu__link"
							@click="toggleMenuCollapse('menu-products-outer', 'menu-products-inner', 'menuProductsDropdownIsOpen')"
						>
							Products
							<div class="menu__down-caret" :class="{ 'menu__down-caret--active': menuProductsDropdownIsOpen }" />
						</a>

						<!-- The products collapse -->
						<div ref="menu-products-outer" class="menu__products">
							<div ref="menu-products-inner" class="menu__products-content">
								<router-link :to="{ name: 'products' }" class="menu__product-link" @click.native="closeAllDropdowns"
									>Shop All</router-link
								>
								<!-- Iterate through all of the product navigation cards -->
								<router-link
									v-for="productClass in productClasses"
									:key="productClass.name"
									:to="{ name: 'products', query: { class: productClass.name } }"
									class="menu__product-link"
									@click.native="closeAllDropdowns"
								>
									{{ productClass.name }}
								</router-link>
							</div>
						</div>

						<!-- Open and close the account collapse -->
						<a
							class="menu__link"
							@click.prevent="toggleMenuCollapse('menu-account-outer', 'menu-account-inner', 'menuAccountDropdownIsOpen')"
						>
							{{ dropdownText }}
							<div class="menu__down-caret" :class="{ 'menu__down-caret--active': menuAccountDropdownIsOpen }" />
						</a>

						<!-- The account/admin collapse -->
						<div ref="menu-account-outer" class="menu__products">
							<div ref="menu-account-inner" class="menu__products-content">
								<!-- Iterate through all of the links, these will either be admin or account links depending upon who the user is -->
								<template v-for="(link, index) in dropdownLinks">
									<router-link
										v-if="linkPermissions[link.permission]"
										:to="{ name: link.url_name }"
										class="menu__product-link"
										:key="index"
										>{{ link.text }}</router-link
									>
								</template>
							</div>
						</div>
					</div>

					<!-- Menu sublinks -->
					<div class="menu__sublinks">
						<router-link :to="{ name: 'settings' }" class="menu__sublink">Settings</router-link>
						<router-link :to="{ name: 'logout' }" class="menu__sublink">Sign Out</router-link>
					</div>
				</div>
			</div>

			<!-- Show a backdrop when a dropdown is open -->
			<transition name="fade">
				<div class="nav__backdrop" v-if="showBackdrop" @click="closeAllDropdowns"></div>
			</transition>
		</div>

		<!-- The slide out cart -->
		<Cart />

		<!-- Adds space depending on the nav theme -->
		<div id="nav-spacer" v-if="!showLightNav" />
	</div>
</template>

<script>
import AvanaLetter from '@/components/icons/AvanaLetter';
import AvanaLogo from '@/components/icons/AvanaLogo';

export default {
	data() {
		return {
			dropDownIsOpen: false,
			scrollPointReached: false,
			accountDropdownIsOpen: false,
			menuDropdownIsOpen: false,
			menuProductsDropdownIsOpen: false,
			menuAccountDropdownIsOpen: false,
			productsDropdownIsOpen: false,
			searchDropdownIsOpen: false,
			showBackdrop: false,
			accountLinks: [
				{ text: 'View company', url_name: 'account.company', permission: 'company' },
				{ text: 'View Stores', url_name: 'account.stores', permission: 'stores' },
				{ text: 'View Orders', url_name: 'account.orders', permission: 'orders' },
				{ text: 'View Users', url_name: 'account.users', permission: 'users' }
			],
			adminLinks: [
				{ text: 'Applications', url_name: 'admin.applications', permission: 'admin' },
				{ text: 'Users', url_name: 'admin.users', permission: 'admin' },
				{ text: 'System', url_name: 'admin.system', permission: 'admin' }
			]
		};
	},
	created() {
		// Listen for scroll event in order to change nav class/state
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
	computed: {
		cartIsOpen() {
			return this.$store.AppState.data.cartIsOpen;
		},
		cartCount() {
			return this.cartItems.map(item => item.quantity).reduce((a, b) => a + b, 0);
		},
		cartItems() {
			return this.$store.AppState.data.cart;
		},
		dropdownLinks() {
			return this.linkPermissions.admin ? this.adminLinks : this.accountLinks;
		},
		dropdownText() {
			return this.linkPermissions.admin ? 'Admin' : 'Account';
		},
		productClasses() {
			// The available product classes for this user's first store's price level
			// Should be populated in App/index if the user is authenticated
			return this.$store.Product.productClasses;
		},
		showLightNav() {
			// The pages where you want the light theme
			const lightNavPages = ['home', '404'];

			// Set the theme depending if it is in the array above
			return lightNavPages.indexOf(this.$route.name) > -1;
		},
		showDarkNav() {
			const darkNavPages = ['account.orders.orderDetails'];

			return darkNavPages.indexOf(this.$route.name) > -1;
		},
		linkPermissions() {
			// Here we can return an object that maps which links we should even show based on the user's permissions
			return {
				company: this.$store.Role.hasGeneralPermission('view company'),
				cart: this.$store.Role.hasGeneralPermission('view cart'),
				orders: this.$store.Role.hasGeneralPermission('view order history'),
				products: this.$store.Role.hasGeneralPermission('view products'),
				stores: this.$store.Role.hasGeneralPermission('view stores'),
				users: this.$store.Role.hasGeneralPermission('view users'),
				admin: Object.keys(this.$store.Role.allPermissionsByRole).indexOf('admin') > -1
			};
		}
	},
	methods: {
		// Closes all the dropdowns when the backdrop is clicked
		closeAllDropdowns() {
			// Set all the dropdownIsOpen variables to false
			this.accountDropdownIsOpen = this.menuDropdownIsOpen = this.productsDropdownIsOpen = this.searchDropdownIsOpen = false;

			// Hide the backdrop
			this.showBackdrop = false;
		},
		// Change the nav class to scroll when you reach a certain point
		handleScroll() {
			this.scrollPointReached = window.scrollY >= 20;
		},
		// Toggles the cart open and close
		toggleCart() {
			this.$store.AppState.set('cartIsOpen', !this.cartIsOpen);
		},
		// This is for a generic collapse that could be used within the mobile menu
		toggleMenuCollapse(outerDiv, innerDiv, collapseBool) {
			// Change the outerDiv height depending on if it is closed or open
			this.$refs[outerDiv].style.height = this[collapseBool] ? `0px` : `${this.$refs[innerDiv].offsetHeight}px`;

			// Toggle the collapseBool
			this[collapseBool] = !this[collapseBool];
		},
		toggleDropdown(name) {
			// List of all the dropdowns
			let dropdowns = ['accountDropdownIsOpen', 'menuDropdownIsOpen', 'productsDropdownIsOpen', 'searchDropdownIsOpen'];

			// Toggle the desired dropdown by name
			this[name] = !this[name];

			// Remove the toggled dropdown
			dropdowns.splice(dropdowns.indexOf(name), 1);

			// Call function to close all other dropdowns and set the backdrop
			this.toggleDropdowns(dropdowns);
		},
		toggleDropdowns(otherDropdowns) {
			// Close all of the dropdowns in the array
			otherDropdowns.forEach(name => {
				this[name] = false;
			});

			// Set the backdrop depending on if a dropdown is open
			this.showBackdrop = this.searchDropdownIsOpen || this.accountDropdownIsOpen || this.menuDropdownIsOpen || this.productsDropdownIsOpen;
		}
	},
	watch: {
		// Close different dropdowns depending on screen width
		'windowSize.width'(newVal) {
			if (newVal >= 992) {
				this.menuDropdownIsOpen = false;
			} else {
				this.searchDropdownIsOpen = false;
				this.accountDropdownIsOpen = false;
				this.productsDropdownIsOpen = false;
			}
		},
		showBackdrop(newVal) {
			if (newVal) {
				this.dropDownIsOpen = newVal;
				document.body.style.overflow = 'hidden';
			} else {
				setTimeout(() => {
					this.dropDownIsOpen = newVal;

					document.body.style.overflow = 'auto';
				}, 400);
			}
		},
		$route() {
			// Reset menu on route change
			this.closeAllDropdowns();
		}
	},
	components: {
		AvanaLetter,
		AvanaLogo,
		Cart: () => import(/* webpackChunkName: "Cart" */ '@/components/App/Cart')
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
@import '@/styles/mixins/navigation.scss';

a,
a:visited,
a:hover,
a:focus,
a:active {
	text-decoration: none;
}
.nav {
	@include Navigation($brand-black, $brand-white, $brand-black, $brand-white);
	position: sticky;
	&--light {
		@include Navigation($brand-white, transparent, $brand-black, $brand-white);
	}
	&--dark {
		@include Navigation($brand-black, $brand-gray-100, $brand-black, $brand-white);
	}
}
#nav-spacer {
	width: 100%;
	height: 60px;
	@include media-breakpoint-up(md) {
		height: 72px;
	}
}
</style>
